import { Injectable, Signal, computed, effect, inject, untracked } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { PagedResponse } from 'mbs-ui-shared@models';
import { PermissionsEnum } from 'mbs-ui-shared@models/PermissionsEnum';
import { DomainInfo, DomainPayload, DomainsQueryParams, UpdateDomainPayload } from 'mbs-ui-shared@models/domain.model';
import { DomainsService } from 'mbs-ui-shared@services/domains.service';
import { DomainStatisticParams } from 'mbs-ui-shared@store/domains/domainsStore.types';
import { Observable, tap } from 'rxjs';
import { DomainsStore } from '../store/domains/domainsStore';
import { PagingAndDataFacade } from '../store/withPagingAndDataStoreFeature/pagingAndData.facade';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DomainsFacade extends PagingAndDataFacade<DomainInfo> {
  private ability = inject(AbilityService);
  private domainsService = inject(DomainsService);
  protected override store = inject(DomainsStore);

  constructor() {
    super();
    effect(() => {
      untracked(() => (this.pagingLoaded() ? this.loadDomainStatistic() : this.store.setDomainStatistic(null)));
    });
  }

  // Custom signals exposed directly
  get restrictedNames(): Signal<string[]> {
    return this.store.selectRestrictedNames;
  }

  get restrictedNamesLoaded(): Signal<boolean> {
    return this.store.selectLoadedRestrictedNames;
  }

  readonly restrictedNamesWhenLoaded: Signal<string[]> = computed(() =>
    this.store.selectLoadedRestrictedNames() ? this.store.selectRestrictedNames() : []
  );

  get domainStatistic() {
    return this.store.selectDomainStatistic;
  }

  get domainStatisticLoaded(): Signal<boolean> {
    return this.store.selectLoadedDomainStatistic;
  }

  get domainStatisticLoading(): Signal<boolean> {
    return this.store.selectLoadingDomainStatistic;
  }

  getDomains(params?: DomainsQueryParams): Observable<PagedResponse<DomainInfo>> {
    return this.domainsService.get(params);
  }

  getByName(name: string): Observable<DomainInfo> {
    return this.domainsService.getByName(name);
  }

  create(payload: DomainPayload[]): Observable<DomainInfo> {
    return this.domainsService.create(payload).pipe(tap(() => this.invalidateEntities()));
  }

  update(payload: UpdateDomainPayload): Observable<DomainInfo> {
    return this.domainsService.update(payload).pipe(tap((domain) => this.invalidateEntities(domain)));
  }

  delete(ids: string[]): Observable<null> {
    return this.domainsService.deleteDomains(ids).pipe(tap(() => this.invalidateEntities()));
  }

  getAvailableDomainNames(payload: string[]): Observable<string[]> {
    return this.domainsService.getAvailableDomainNames(payload);
  }

  loadDomainStatistic(params?: DomainStatisticParams): void {
    const { force = false, companyId } = params || {};

    this.store.loadDomainStatistic({ companyId, force });
  }

  loadRestrictedNames(force = false): void {
    this.store.loadRestrictedNames({ force });
  }

  exportTo(type: 'html' | 'csv'): void {
    this.domainsService.exportTo(type);
  }

  checkSpecificAccessAppsUsers(companyId: string): boolean {
    return (
      this.ability.can('read', 'Provider') ||
      (this.ability.can('read', 'SubAdmin') &&
        this.ability.can('read', PermissionsEnum.GoogleApps) &&
        (this.ability.can('read', PermissionsEnum.AccessToAllCompanies) ||
          (!this.ability.can('read', PermissionsEnum.AccessToAllCompanies) && companyId != null)))
    );
  }

  private invalidateEntities(domain?: DomainInfo): void {
    this.refreshAfterCRUD(domain, 'domainName');
  }
}
